// VOID Conflict — Graceful Update & Cache Orchestrator
//
// Coordinates seamless, zero-data-loss application updates across:
// - Android WebAPK / iOS Home Screen PWAs (which stay suspended in RAM across app switches)
// - Mobile & Desktop Browsers
//
// Guarantees:
// 1. Active matches (Online 1v1/2v2, Singleplayer, Campaign, Exhibition) and pack openings
//    are NEVER interrupted or overlaid.
// 2. Local account credentials & game state in localStorage are NEVER cleared.
// 3. Pending OCC Cloud Saves (pushSave) are flushed (with a 1.2s safety timeout) before reload.
// 4. Dual-Bucket Cache preservation: only `*-shell` caches are rotated on code updates;
//    `void-media-*` card art & audio caches remain intact (0 MB unnecessary re-download).

import { pushSave } from '../net/cloudSync.js';
import { BALANCE_HASH } from '../data/cards.js';

export const LOOP_GUARD_COOLDOWN_MS = 15_000;
export const CHECK_THROTTLE_MS = 60_000;
export const IDLE_INPUT_GRACE_MS = 3_000;
export const STORAGE_KEY_RELOAD_TS = 'void_last_reload_ts';
export const STORAGE_KEY_RELOAD_HASH = 'void_last_reload_hash';

let _game = null;
let _updatePending = false;
let _detectedRemoteHash = null;
let _lastCheckTs = 0;
let _lastUserInteractionTs = 0;
let _isReloading = false;
let _initialized = false;

/**
 * Reads the current build hash stamped into `<meta name="void-build-hash">` by scripts/build.mjs.
 * Returns `'dev'` in unbundled local development.
 */
export function getBuildHash(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc || typeof doc.querySelector !== 'function') return 'dev';
  const meta = doc.querySelector('meta[name="void-build-hash"]');
  const val = meta?.getAttribute?.('content') || meta?.content || '';
  return val.trim() || 'dev';
}

/**
 * Concise build/balance tag for Start Screen footer (e.g. "E34D55").
 */
export function getStartScreenBuildTag(doc = typeof document !== 'undefined' ? document : null) {
  const balShort = (BALANCE_HASH || '').slice(0, 6).toUpperCase();
  if (balShort) return balShort;
  const hash = getBuildHash(doc);
  return (hash && hash !== 'dev') ? hash.slice(0, 6).toUpperCase() : 'DEV';
}

/**
 * Human-readable build telemetry string for Options Modal.
 */
export function getFormattedBuildTag(doc = typeof document !== 'undefined' ? document : null) {
  const hash = getBuildHash(doc);
  const balShort = (BALANCE_HASH || '').slice(0, 6).toUpperCase();
  if (!hash || hash === 'dev') {
    return balShort ? `BUILD #DEV · BAL #${balShort}` : 'BUILD #DEV-LOCAL';
  }
  return balShort ? `BUILD #${hash.toUpperCase()} · BAL #${balShort}` : `BUILD #${hash.toUpperCase()}`;
}

/**
 * Extracts `<meta name="void-build-hash" content="...">` from raw HTML text.
 */
export function extractBuildHashFromHtml(htmlText) {
  if (typeof htmlText !== 'string' || !htmlText) return null;
  const match = htmlText.match(/<meta\s+name=["']void-build-hash["']\s+content=["']([^"']+)["']/i)
    || htmlText.match(/<meta\s+content=["']([^"']+)["']\s+name=["']void-build-hash["']/i);
  return match ? match[1].trim() : null;
}

/**
 * Returns true if the player is currently in an active match or critical animation
 * (pack tearing/revealing, slot machine spin, cutscene) where any interruption is forbidden.
 */
export function isMatchOrCriticalActionActive(game = _game) {
  if (!game) return false;

  // 1. Any running battlefield simulation (Singleplayer, Online, Exhibition, Campaign)
  if (game.isRunning) return true;
  if (typeof game.isOnlineMatchLive === 'function' && game.isOnlineMatchLive()) return true;
  if (game.isOnline && game.wsClient?.isMatchActive) return true;
  if (game.cutscenePlayer?.active) return true;

  // 2. Pack Opening Modal during active tear/reveal/warp
  if (game.packOpeningModal?.isOpen?.()) {
    const packState = game.packOpeningModal._state;
    if (
      game.packOpeningModal._warpActive ||
      packState === 'tearing' ||
      packState === 'exploding' ||
      packState === 'revealing' ||
      packState === 'cards_reveal'
    ) {
      return true;
    }
  }

  // 3. Slot Machine during active spin
  if (game.slotMachineModal?.isOpen?.() && game.slotMachineModal._isSpinning) {
    return true;
  }

  return false;
}

/**
 * Returns true ONLY when the user is idle on the initial Start Screen with no modals open
 * and no recent touch/click input, allowing a seamless Zero-Click update reload.
 */
export function canAutoReloadSafely(game = _game, now = Date.now()) {
  if (isMatchOrCriticalActionActive(game)) return false;

  // Recent user interaction guard: never auto-reload while user is actively tapping
  if (_lastUserInteractionTs > 0 && now - _lastUserInteractionTs < IDLE_INPUT_GRACE_MS) {
    return false;
  }

  if (typeof document !== 'undefined') {
    const startScreenEl = document.getElementById?.('start-screen');
    const isStartActive = startScreenEl
      ? startScreenEl.classList?.contains?.('active')
      : !!game?.startScreen?.isVisible;
    if (!isStartActive) return false;

    // Do not auto-reload if any fullscreen submenu (Options, Profile, Ladder, Legal) is open
    if (document.querySelector?.('.fullscreen-dialog-overlay')) return false;
    if (document.getElementById?.('game-over-modal')?.classList?.contains?.('active')) return false;
    if (document.getElementById?.('online-modal')?.classList?.contains?.('active')) return false;
    if (document.getElementById?.('deck-modal')?.classList?.contains?.('active')) return false;
  } else if (!game?.startScreen?.isVisible) {
    return false;
  }

  if (game?.overlayGate?.isOpen?.()) return false;
  if (game?.onlineLobby?.isOpen) return false;
  if (game?.deckBuilder?.isOpen) return false;
  if (game?.packOpeningModal?.isOpen?.()) return false;
  if (game?.slotMachineModal?.isOpen?.()) return false;
  if (game?.showroomModal?.isOpen?.()) return false;

  return true;
}

/**
 * Checks sessionStorage loop guard to guarantee we never enter an infinite reload loop.
 */
export function isLoopGuardTriggered(targetHash = null, now = Date.now(), storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null) {
  if (!storage) return false;
  try {
    const lastTs = Number(storage.getItem(STORAGE_KEY_RELOAD_TS) || 0);
    if (lastTs > 0 && now - lastTs < LOOP_GUARD_COOLDOWN_MS) {
      return true;
    }
    if (targetHash && targetHash !== 'dev') {
      const lastHash = storage.getItem(STORAGE_KEY_RELOAD_HASH);
      if (lastHash === targetHash && lastTs > 0 && now - lastTs < 60_000) {
        return true;
      }
    }
  } catch (_) {}
  return false;
}

/**
 * Records reload timestamp and target hash in sessionStorage before reloading.
 */
export function markReloadInSession(targetHash = null, now = Date.now(), storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null) {
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY_RELOAD_TS, String(now));
    if (targetHash) {
      storage.setItem(STORAGE_KEY_RELOAD_HASH, String(targetHash));
    }
  } catch (_) {}
}

/**
 * Purges only `*-shell` caches in CacheStorage while keeping `void-media-*` / `*-assets` untouched.
 */
export async function clearShellCachesOnly(cachesApi = typeof caches !== 'undefined' ? caches : null) {
  if (!cachesApi || typeof cachesApi.keys !== 'function') return [];
  try {
    const keys = await cachesApi.keys();
    const deleted = [];
    for (const key of keys) {
      if (key.endsWith('-shell')) {
        await cachesApi.delete(key);
        deleted.push(key);
      }
    }
    return deleted;
  } catch (_) {
    return [];
  }
}

/**
 * Flushes pending OCC Cloud Save (bounded by 1200ms timeout), clears stale shell cache if needed,
 * activates any waiting Service Worker, and reloads the application cleanly.
 */
export async function applyUpdateAndReload(options = {}) {
  const {
    force = false,
    clearShellCache = true,
    targetHash = _detectedRemoteHash,
    reloadFn = null,
    syncFn = pushSave,
    storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null,
  } = options;

  if (_isReloading) return false;
  const now = Date.now();
  if (!force && isLoopGuardTriggered(targetHash, now, storage)) {
    return false;
  }

  _isReloading = true;
  markReloadInSession(targetHash || getBuildHash(), now, storage);

  // Visual feedback on banner if visible
  if (typeof document !== 'undefined') {
    const bannerText = document.getElementById?.('void-update-banner-text');
    if (bannerText) {
      bannerText.textContent = '⬡ SYNCHRONIZING & UPDATING SYSTEM...';
    }
  }

  // 1. Flush OCC Cloud Save so recent deck edits or card pulls are safely persisted
  try {
    if (typeof syncFn === 'function') {
      await Promise.race([
        Promise.resolve(syncFn()),
        new Promise((resolve) => setTimeout(resolve, 1200))
      ]);
    }
  } catch (_) {}

  // 2. Purge only the lightweight App-Shell cache (preserving 40-80 MB of card/audio assets)
  if (clearShellCache) {
    await clearShellCachesOnly();
  }

  // 3. Tell waiting Service Worker (if any) to skipWaiting
  try {
    if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
      const reg = await (window?.__swRegistrationPromise || navigator.serviceWorker.getRegistration?.());
      if (reg?.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
      if (force && reg?.update) {
        await Promise.race([
          reg.update().catch(() => {}),
          new Promise((resolve) => setTimeout(resolve, 800))
        ]);
      }
    }
  } catch (_) {}

  // 4. Trigger reload
  if (typeof reloadFn === 'function') {
    reloadFn();
  } else if (typeof window !== 'undefined' && window.location?.reload) {
    window.location.reload();
  }
  return true;
}

/**
 * Hides the non-blocking update banner if present.
 */
export function hideUpdateBanner() {
  if (typeof document === 'undefined') return;
  const el = document.getElementById?.('void-update-banner');
  if (el) {
    el.classList?.remove?.('visible');
    el.setAttribute?.('aria-hidden', 'true');
  }
}

/**
 * Renders and reveals the non-blocking Sci-Fi Update Pill in the top center of the screen.
 * Strictly suppressed during active matches or pack opening animations.
 */
export function showUpdateBanner(options = {}) {
  const game = options.game ?? _game;
  if (isMatchOrCriticalActionActive(game)) {
    _updatePending = true;
    hideUpdateBanner();
    return false;
  }

  if (typeof document === 'undefined' || !document.body) return false;

  let banner = document.getElementById('void-update-banner');
  if (!banner) {
    banner = document.createElement('div');
    banner.id = 'void-update-banner';
    banner.className = 'void-update-banner';
    banner.setAttribute('role', 'status');
    banner.setAttribute('aria-live', 'polite');
    banner.innerHTML = `
      <button type="button" id="void-update-banner-btn" class="void-update-banner-btn" title="Tap to apply update and reload">
        <span class="void-update-banner-dot" aria-hidden="true"></span>
        <span id="void-update-banner-text" class="void-update-banner-text">SYSTEM UPDATE READY — TAP TO RELOAD</span>
      </button>
      <button type="button" id="void-update-banner-dismiss" class="void-update-banner-dismiss" aria-label="Dismiss update notice" title="Later">×</button>
    `;
    document.body.appendChild(banner);

    const applyBtn = banner.querySelector('#void-update-banner-btn');
    applyBtn?.addEventListener('click', () => {
      _game?.audio?.playUiClick?.();
      applyUpdateAndReload({ force: true, clearShellCache: true });
    });

    const dismissBtn = banner.querySelector('#void-update-banner-dismiss');
    dismissBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      _game?.audio?.playUiClick?.();
      hideUpdateBanner();
    });
  }

  if (options.message) {
    const textEl = banner.querySelector('#void-update-banner-text');
    if (textEl) textEl.textContent = options.message;
  }

  banner.classList.add('visible');
  banner.setAttribute('aria-hidden', 'false');
  return true;
}

/**
 * Handles a confirmed update (either via Service Worker lifecycle or HTML build-hash mismatch).
 */
export async function handleUpdateDetected(remoteHash = null, options = {}) {
  const game = options.game ?? _game;
  if (remoteHash) {
    _detectedRemoteHash = remoteHash;
  }
  _updatePending = true;

  // 1. If in a match or pack opening: stay completely silent and defer until return to idle
  if (isMatchOrCriticalActionActive(game)) {
    hideUpdateBanner();
    return { action: 'deferred_in_match' };
  }

  // 2. If idle on the Start Screen with no modals open: perform seamless Zero-Click update
  if (canAutoReloadSafely(game)) {
    const reloaded = await applyUpdateAndReload({
      force: false,
      clearShellCache: true,
      targetHash: _detectedRemoteHash,
      reloadFn: options.reloadFn,
      syncFn: options.syncFn,
      storage: options.storage,
    });
    if (reloaded) {
      return { action: 'auto_reloaded' };
    }
  }

  // 3. Otherwise (in Hub, Deckbuilder, Showroom, Options): show non-blocking Opt-In Pill
  showUpdateBanner({ game });
  return { action: 'banner_shown' };
}

/**
 * Called when a match ends or the player returns to the main menu / hub,
 * so any update deferred during combat can now be surfaced or applied.
 */
export function notifyUpdateManagerIdle(game = _game) {
  if (_game && !game) game = _game;
  if (!_updatePending) return false;
  if (isMatchOrCriticalActionActive(game)) return false;

  if (canAutoReloadSafely(game)) {
    applyUpdateAndReload({ force: false, clearShellCache: true, targetHash: _detectedRemoteHash });
    return true;
  }
  return showUpdateBanner({ game });
}

/**
 * Called if the online matchmaking server rejects a match due to a BALANCE_HASH version mismatch.
 */
export function notifyVersionMismatch(serverMessage = '') {
  _updatePending = true;
  showUpdateBanner({
    message: serverMessage
      ? 'NEW GAME VERSION REQUIRED FOR ONLINE PLAY — TAP TO UPDATE'
      : 'SYSTEM UPDATE READY — TAP TO RELOAD'
  });
}

/**
 * Actively checks both the Service Worker registration and `./index.html`'s `void-build-hash`
 * meta tag to detect newly deployed builds on `voidconflict.com`.
 */
export async function checkForAppUpdate(options = {}) {
  const {
    force = false,
    fetchFn = typeof fetch === 'function' ? fetch : null,
    game = _game,
  } = options;

  // Never check while offline
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { updated: false, reason: 'offline' };
  }

  // Never generate network traffic during an active match
  if (!force && isMatchOrCriticalActionActive(game)) {
    return { updated: false, reason: 'match_active' };
  }

  const now = Date.now();
  if (!force && _lastCheckTs > 0 && now - _lastCheckTs < CHECK_THROTTLE_MS) {
    return { updated: false, reason: 'throttled' };
  }
  _lastCheckTs = now;

  // 1. Trigger Service Worker update check
  try {
    if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
      const reg = await (window?.__swRegistrationPromise || navigator.serviceWorker.getRegistration?.());
      if (reg) {
        if (reg.waiting) {
          await handleUpdateDetected(_detectedRemoteHash, options);
          return { updated: true, source: 'sw_waiting' };
        }
        await reg.update().catch(() => {});
        if (reg.waiting) {
          await handleUpdateDetected(_detectedRemoteHash, options);
          return { updated: true, source: 'sw_update' };
        }
      }
    }
  } catch (_) {}

  // 2. Compare `<meta name="void-build-hash">` against live `./index.html` (cache: 'no-store')
  const currentHash = getBuildHash();
  if (currentHash && currentHash !== 'dev' && typeof fetchFn === 'function') {
    try {
      const res = await fetchFn(`./index.html?_vc=${now}`, {
        method: 'GET',
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' }
      });
      if (res && res.ok) {
        const htmlText = await res.text();
        const remoteHash = extractBuildHashFromHtml(htmlText);
        if (remoteHash && remoteHash !== 'dev' && remoteHash !== currentHash) {
          await handleUpdateDetected(remoteHash, options);
          return { updated: true, source: 'meta_hash', currentHash, remoteHash };
        }
      }
    } catch (_) {}
  }

  return { updated: false, reason: 'up_to_date' };
}

/**
 * Injects the subtle build telemetry badge into the Start Screen footer if not already present.
 */
export function syncStartScreenBuildBadge(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc || typeof doc.getElementById !== 'function') return;
  const footer = doc.querySelector?.('.start-screen-footer');
  if (!footer) return;

  let badge = doc.getElementById('start-build-tag');
  if (!badge) {
    const sep = doc.createElement('span');
    sep.className = 'start-footer-sep';
    sep.textContent = '//';
    badge = doc.createElement('a');
    badge.id = 'start-build-tag';
    badge.className = 'start-footer-build-tag start-footer-link';
    badge.href = 'https://discord.gg/UECTSvDB4u';
    badge.target = '_blank';
    badge.rel = 'noopener noreferrer';
    badge.title = 'Join the VOID Conflict Discord';
    footer.appendChild(sep);
    footer.appendChild(badge);
  } else if (badge.tagName === 'A' && !badge.getAttribute('href')) {
    badge.setAttribute('href', 'https://discord.gg/UECTSvDB4u');
    badge.setAttribute('target', '_blank');
    badge.setAttribute('rel', 'noopener noreferrer');
    badge.setAttribute('title', 'Join the VOID Conflict Discord');
  }
  badge.textContent = getStartScreenBuildTag(doc);
}

/**
 * Initializes the Update Manager and binds lifecycle listeners (`visibilitychange`, SW events).
 */
export function initUpdateManager(game) {
  _game = game;
  syncStartScreenBuildBadge();

  if (_initialized) return;
  _initialized = true;

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Track user interactions so Zero-Click reload never fires mid-tap
  const recordInteraction = () => {
    _lastUserInteractionTs = Date.now();
  };
  window.addEventListener('pointerdown', recordInteraction, { passive: true, capture: true });
  window.addEventListener('keydown', recordInteraction, { passive: true, capture: true });

  // Attach Service Worker updatefound & controllerchange listeners
  if ('serviceWorker' in navigator) {
    const hadControllerAtBoot = !!navigator.serviceWorker.controller;

    navigator.serviceWorker.addEventListener?.('controllerchange', () => {
      // Only trigger update handling if a previous controller was replaced (not first-time install)
      if (hadControllerAtBoot) {
        handleUpdateDetected(_detectedRemoteHash);
      }
    });

    Promise.resolve(window.__swRegistrationPromise || navigator.serviceWorker.getRegistration?.())
      .then((reg) => {
        if (!reg) return;
        if (reg.waiting && navigator.serviceWorker.controller) {
          handleUpdateDetected(_detectedRemoteHash);
        }
        reg.addEventListener?.('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener?.('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              handleUpdateDetected(_detectedRemoteHash);
            }
          });
        });
      })
      .catch(() => {});
  }

  // Check for updates when PWA / mobile browser wakes up from background RAM suspension
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      if (_updatePending) {
        notifyUpdateManagerIdle(_game);
      } else {
        checkForAppUpdate();
      }
    }
  });

  window.addEventListener('online', () => {
    if (_updatePending) {
      notifyUpdateManagerIdle(_game);
    } else {
      checkForAppUpdate();
    }
  });

  // Initial post-boot update check (non-blocking after 2.5s)
  setTimeout(() => {
    checkForAppUpdate();
  }, 2500);
}

/**
 * Resets internal state for unit tests.
 */
export function _resetUpdateManagerForTests() {
  _game = null;
  _updatePending = false;
  _detectedRemoteHash = null;
  _lastCheckTs = 0;
  _lastUserInteractionTs = 0;
  _isReloading = false;
  _initialized = false;
}
